import { assertUnreachable, isDefined } from '@sgme/fp';

import { useFlexiForwardSelector } from '@/App/tiles/FlexiForward/FlexiForwardContext.ts';
import { Awaiting } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardPriceSummary/Awaiting';
import { NotPriceable } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardPriceSummary/NotPriceable';
import { Priceable } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardPriceSummary/Priceable.tsx';
import { Pricing } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardPriceSummary/Pricing';
import { StreamingError } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardPriceSummary/StreamingError';
import { getFlexiForwardQuote } from '@/store/flexi-forward/helpers/rfs.ts';
import { selectFlexiForwardRfsStatus } from '@/store/flexi-forward/selectors/rfs';
import type { FlexiForwardRfsStatus } from '@/store/flexi-forward/state';

function getPriceSummaryContent(tileId: string, rfsStatus: FlexiForwardRfsStatus, hasQuote: boolean) {
  switch (rfsStatus) {
    case 'NOT_PRICEABLE':
      return <NotPriceable />;
    case 'PRICEABLE': {
      if (isDefined(hasQuote)) {
        return <Pricing tileId={tileId} />;
      }
      return <Priceable />;
    }
    case 'AWAITING_INDICATIVE':
      return <Awaiting forceTradeable={false} />;
    case 'AWAITING_EXECUTABLE':
      return <Awaiting forceTradeable={true} />;
    case 'STREAMING_INDICATIVE':
      return <Pricing tileId={tileId} />;
    case 'STREAMING_EXECUTABLE':
      return <Pricing tileId={tileId} isExecutable />;
    case 'ERROR':
    // TODO ABO, manage these cases
    case 'BYPASSABLE_ERROR':
      return <StreamingError />;
    default:
      return assertUnreachable(rfsStatus, `Unknown price status: ${rfsStatus}`);
  }
}

interface FlexiForwardPriceSummaryProps {
  tileId: string;
}

export function FlexiForwardPriceSummary({ tileId }: FlexiForwardPriceSummaryProps) {
  const { hasQuote, status } = useFlexiForwardSelector((flexiForward) => ({
    hasQuote: isDefined(getFlexiForwardQuote(flexiForward.properties.rfs)),
    status: selectFlexiForwardRfsStatus(flexiForward),
  }));

  return (
    <div style={{ height: '100px' }} data-e2e="price-summary">
      {getPriceSummaryContent(tileId, status, hasQuote)}
    </div>
  );
}
